import { inject, isSignal } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { SessionCacheService } from '@copado/cds';
import { Providers } from '@crt/platform-types/dist/enum';
import { Logger } from '@crt/sui';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { firstValueFrom, from } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';

import { AppConstants } from '@app/app.constants.service';
import { UpdateEvent, UserState } from '@app/auth/user.state';
import { BootstrapSuffix } from '@app/common.model';
import { ORG_ID_PARAM } from '@app/shared/app-router.service';
import { LoginProviderService } from '@app/shared/login-provider.service';
import { ToastService } from '@app/toasts/toast.service';

import { AppActivatedRouteSnapshot } from '../app-routing.interface';

let isFirstRun = true;

/**
 * currentOrgGuard makes sure that the current org observable is switched to
 * the organization specified by path parameter.
 *
 * This will let routing to continue only after org switch.
 * By default we're expecting that the path parameter for route is `id`,
 * but you can use data.params.orgId to override the parameter name.
 */
export const currentOrgGuard: CanActivateFn = (route: AppActivatedRouteSnapshot) => {
  const loginProvider = inject(LoginProviderService);
  const modalService = inject(NgbModal);
  const router = inject(Router);
  const cache = inject(SessionCacheService);
  const toastService = inject(ToastService);
  const userState = inject(UserState);

  const user = userState.user();
  const param = route.data.params?.orgId;
  let orgId = +(route.queryParams[ORG_ID_PARAM] ?? route.params[param ?? 'orgId']);

  if (!orgId) {
    const currentOrg = cache.get<number>(AppConstants.CACHE_KEYS.CURRENT_ORG_ID);
    if (currentOrg && userState.orgs().find((org) => org.id === currentOrg)) {
      orgId = currentOrg;
    } else if (user?.defaultOrgId && userState.orgs().find((org) => org.id === user.defaultOrgId)) {
      orgId = user.defaultOrgId;
    } else {
      orgId = userState.orgs()[0]?.id ?? 0;
    }
  }

  if (orgId) {
    const org = userState.orgs().find((o) => o.id === orgId);

    const needsProviderCheck = !!(
      org?.allowedLoginProviders.length &&
      !user?.superAdmin &&
      !user?.provisioningAdmin
    );

    // TODO (lovell): LoginProviderService needs to go. See: US-0611352 and linked ADI discussion.
    const currentProvider = userState.user()?.provider ?? loginProvider.provider();

    if (needsProviderCheck && !hasProviderAccess(org, currentProvider)) {
      userState.updateReturnTo$.next(`/?${ORG_ID_PARAM}=${orgId}`);

      return from(
        showProviderModal(org, currentProvider, modalService, !!userState.org() && !isFirstRun),
      ).pipe(map(() => false));
    }

    isFirstRun = false;

    if (orgId === userState.orgId()) {
      return true;
    }

    userState.switchOrg$.next(orgId);
    return firstValueFrom(
      userState.orgSwitchingEvent$.pipe(
        take(1),
        map((event) =>
          event === UpdateEvent.Success ? true : router.parseUrl(route.data.routeOnError ?? '/'),
        ),
        tap((permission) => {
          if (permission !== true) {
            toastService.show({
              title: $localize`Error!`,
              text: $localize`Organization doesn't exist or you don't have access to it`,
              type: BootstrapSuffix.Danger,
            });
          }
        }),
      ),
    );
  }
  Logger.warn('currentOrgGuard: no orgId found in route or query params');
  return true;
};

function hasProviderAccess(
  org: { allowedLoginProviders: Providers[] },
  current: Providers,
): boolean {
  const allowed = org.allowedLoginProviders.includes(Providers.Local)
    ? [...org.allowedLoginProviders, Providers.LocalMFA]
    : org.allowedLoginProviders;
  return allowed.includes(current);
}

async function showProviderModal(
  org: { allowedLoginProviders: Providers[] },
  current: Providers,
  modal: NgbModal,
  canClose: boolean,
): Promise<void> {
  const wantsMfaSetup =
    current === Providers.Local && org.allowedLoginProviders.includes(Providers.LocalMFA);

  const { IncorrectLoginProviderModalComponent } =
    await import('../auth/incorrect-login-provider-modal/incorrect-login-provider-modal.component');
  const { AddMfaModalComponent } = await import('../auth/add-mfa-modal/add-mfa-modal.component');

  const modalComponent = wantsMfaSetup
    ? AddMfaModalComponent
    : IncorrectLoginProviderModalComponent;

  const ref = modal.open(modalComponent, {
    backdrop: canClose ? true : 'static',
    size: 'md',
  });
  const componentInstance = ref.componentInstance;

  if (isSignal(componentInstance.org)) {
    componentInstance.org.set(org);
  }
  if (isSignal(componentInstance.canClose)) {
    componentInstance.canClose.set(canClose);
  }
}
